import { Heading } from '@workwhile/ui'
import { useAuthContext } from 'components/auth'
import {
  Ban,
  Boxes,
  Building2,
  ClipboardCheck,
  Command,
  HardHat,
  MapPin,
  Package,
  Scale,
  ScrollText,
  SearchIcon,
  Scroll,
} from 'lucide-react'
import { getRouteWithDefaultQueryParams } from 'pages/Shifts/Shifts'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import {
  MdAttachMoney,
  MdCardMembership,
  MdChecklist,
  MdFolderOpen,
  MdHouse,
  MdList,
  MdMenu,
  MdMessage,
  MdOutlinePayment,
  MdPerson,
  MdPhone,
  MdReceipt,
} from 'react-icons/md'
import { Menu, MenuItem, Sidebar, SubMenu } from 'react-pro-sidebar'
import { useMediaQuery } from 'react-responsive'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import logo from '../../assets/ww_logo.png'
import * as ROUTES from '../../constants/routes'
import SignOutButton from '../SignOut'
import './nav.css'
const theme = {
  sidebar: {
    backgroundColor: '#0b2948',
    color: '#607489',
  },
  menu: {
    menuItemText: '#27C8B6',
    icon: '#27C8B6',
    hover: {
      backgroundColor: '#c5e4ff',
      color: '#44596e',
    },
    disabled: {
      color: '#9fb6cf',
    },
  },
}

const menuItemStyles = {
  root: {
    fontSize: '13px',
    fontWeight: 400,
  },
  icon: {
    color: theme.menu.icon,
  },
  SubMenuExpandIcon: {
    color: '#b6b7b9',
  },
  subMenuContent: () => ({
    backgroundColor: theme.sidebar.backgroundColor,
  }),
  button: {
    color: theme.menu.menuItemText,
    '&:hover': {
      backgroundColor: 'rgba(197, 228, 255, 1)',
      color: theme.menu.hover.color,
    },
  },
  label: ({ open }: { open?: boolean }) => ({
    fontWeight: open ? 600 : undefined,
    color: theme.menu.menuItemText,
  }),
}

const SidebarNav = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [collapsed, setCollapsed] = useState(isMobile)
  const { isAuthenticated } = useAuthContext()

  return (
    <Sidebar
      rootStyles={{
        color: theme.sidebar.color,
        overflowY: 'hidden',
        height: '100%',
      }}
      width="300px"
      backgroundColor={theme.sidebar.backgroundColor}
      collapsed={collapsed}
    >
      {isAuthenticated ? (
        <SidebarNavAuth collapsed={collapsed} setCollapsed={setCollapsed} />
      ) : (
        <NavigationNonAuth />
      )}
    </Sidebar>
  )
}

const SidebarNavAuth = ({ collapsed, setCollapsed }) => {
  const toggleSidebar = () => {
    setCollapsed(!collapsed)
  }

  const openCommandPalette = () => {
    // simulate ctrl+k keypress to open command palette
    window.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'k',
        ctrlKey: true,
        bubbles: true,
        cancelable: true,
      })
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <HamburgerContainer>
        <HamburgerButton onClick={toggleSidebar}>
          <MdMenu size={24} color={theme.menu.icon} />
        </HamburgerButton>
      </HamburgerContainer>
      <StyledSidebarHeader>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={logo}
            style={{ width: '30px', height: '30px' }}
            alt="Logo"
          />
          <Heading level={2} ml={3} color="white">
            WorkWhile
          </Heading>
        </div>
      </StyledSidebarHeader>

      <CommandPaletteInputContainer collapsed={collapsed}>
        {collapsed ? (
          <CollapsedSearchButton
            onClick={openCommandPalette}
            title="Command Palette"
          >
            <SearchIcon size={16} color="#999" />
          </CollapsedSearchButton>
        ) : (
          <>
            <CommandPaletteInput
              onClick={openCommandPalette}
              onFocus={openCommandPalette}
              readOnly
              placeholder="Search..."
            />
            <SearchIconWrapper>
              <SearchIcon size={16} />
            </SearchIconWrapper>
            <ShortcutHint>
              {navigator.userAgent.indexOf('Mac') >= 0 ? '⌘ + K' : 'ctrl + K'}
            </ShortcutHint>
          </>
        )}
      </CommandPaletteInputContainer>

      <Menu menuItemStyles={menuItemStyles}>
        <MenuItem
          component={<Link to="/users" />}
          icon={<MdPerson size={15} />}
        >
          Users
        </MenuItem>
        <SubMenu
          label="Shifts"
          icon={<ScrollText size={15} />}
          rootStyles={{ color: theme.menu.menuItemText }}
        >
          <MenuItem
            icon={<HardHat size={15} />}
            component={
              <Link
                to={getRouteWithDefaultQueryParams()}
                className="nav-link"
              />
            }
          >
            Shifts
          </MenuItem>
          <MenuItem
            icon={<Ban size={15} />}
            component={
              <Link to={ROUTES.UPCOMING_SHIFTS} className="nav-link" />
            }
          >
            Upcoming Shifts
          </MenuItem>
          <MenuItem
            icon={<Ban size={15} />}
            component={<Link to={ROUTES.ONGOING_SHIFTS} className="nav-link" />}
          >
            Ongoing Shifts
          </MenuItem>
          <MenuItem
            icon={<Ban size={15} />}
            component={<Link to={ROUTES.PAST_SHIFTS} className="nav-link" />}
          >
            Past Shifts
          </MenuItem>
          <MenuItem
            icon={<Ban size={15} />}
            component={<Link to={ROUTES.SINGLE_SHIFT} className="nav-link" />}
          >
            Single Shift
          </MenuItem>
          <MenuItem
            icon={<Ban size={15} />}
            component={
              <Link to={ROUTES.BULK_CREATE_SHIFTS} className="nav-link" />
            }
          >
            Bulk Create Shifts
          </MenuItem>
        </SubMenu>

        <SubMenu label="Company" icon={<MdHouse size={15} />}>
          <MenuItem
            component={<Link to={ROUTES.COMPANIES} className="nav-link" />}
            icon={<Building2 size={15} />}
          >
            Companies
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.COMPANY_GROUPS} className="nav-link" />}
            icon={<Package size={15} />}
          >
            Company Groups
          </MenuItem>
        </SubMenu>
        <MenuItem
          component={<Link to={ROUTES.POSITIONS} className="nav-link" />}
          icon={<MdPhone size={15} />}
        >
          Positions
        </MenuItem>
        <MenuItem
          component={<Link to={ROUTES.MESSAGES2} className="nav-link" />}
          icon={<MdMessage size={15} />}
        >
          Messages
        </MenuItem>
        <SubMenu label="Payments" icon={<MdOutlinePayment size={15} />}>
          <MenuItem
            component={<Link to={ROUTES.USER_PAYMENTS} className="nav-link" />}
            icon={<MdPerson size={15} />}
          >
            User Payments
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.UNPAID_SHIFTS} className="nav-link" />}
            icon={<MdChecklist size={15} />}
          >
            Unpaid Shifts
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.INVOICES} className="nav-link" />}
            icon={<MdHouse size={15} />}
          >
            Invoices
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.CHARGES} className="nav-link" />}
            icon={<MdReceipt size={15} />}
          >
            Charges
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.BILL_RATES} className="nav-link" />}
            icon={<MdAttachMoney size={15} />}
          >
            Bill Rates
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.INVOICE_CONFIG} className="nav-link" />}
            icon={<MdReceipt size={15} />}
          >
            Invoice Config
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.INVOICE_ISSUES} className="nav-link" />}
            icon={<MdChecklist size={15} />}
          >
            Invoice Issues
          </MenuItem>
        </SubMenu>
        <SubMenu label="Review" icon={<MdList size={15} />}>
          <MenuItem
            component={<Link to={ROUTES.REVIEW} className="nav-link" />}
            icon={<MdChecklist size={15} />}
          >
            Admin Review
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.REVIEW_ASSESSMENTS_QUEUE} className="nav-link" />
            }
            icon={<ClipboardCheck size={15} />}
          >
            Assessments Queue
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.VERIFY_CREDENTIALS} className="nav-link" />
            }
            icon={<MdCardMembership size={15} />}
          >
            Verify Credentials
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.REVIEW_BLOCK_WORKER} className="nav-link" />
            }
            icon={<Ban size={15} />}
          >
            Block/Unblock Worker
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.EVENT_LOGS} className="nav-link" />}
            icon={<ScrollText size={15} />}
          >
            Event logs
          </MenuItem>
        </SubMenu>
        <SubMenu label="W2" icon={<MdList size={15} />}>
          <MenuItem
            component={<Link to={ROUTES.W2_WORK_STATUS} className="nav-link" />}
            icon={<MdChecklist size={15} />}
          >
            Work Status Summary
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.W2_PENDING_PAYMENTS} className="nav-link" />
            }
            icon={<MdChecklist size={15} />}
          >
            Pending Payments
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.W2_ITEMS_IN_REVIEW} className="nav-link" />
            }
            icon={<MdChecklist size={15} />}
          >
            Items In Review
          </MenuItem>
        </SubMenu>
        <SubMenu label="Wage Rules" icon={<Scale size={15} />}>
          <MenuItem
            component={
              <Link to={ROUTES.WAGE_JURISDICTIONS} className="nav-link" />
            }
            icon={<MapPin size={15} />}
          >
            Jurisdictions
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.WAGE_RULES} className="nav-link" />}
            icon={<Scroll size={15} />}
          >
            Rules
          </MenuItem>
        </SubMenu>
        <MenuItem
          component={<Link to={ROUTES.COMMANDS} className="nav-link" />}
          icon={<Command size="15" />}
        >
          Commands
        </MenuItem>

        <MenuItem
          component={<Link to={ROUTES.POSITION_EDITOR} className="nav-link" />}
          icon={<MdFolderOpen size={15} />}
        >
          Position Editor
        </MenuItem>

        <SubMenu label="VMS" icon={<Boxes size={15} />}>
          <MenuItem
            component={<Link to={ROUTES.DAYFORCE} className="nav-link" />}
          >
            Dayforce
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.INDEED_FLEX} className="nav-link" />}
          >
            Indeed Flex
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.FIELDGLASS} className="nav-link" />}
          >
            Fieldglass
          </MenuItem>
          <MenuItem component={<Link to={ROUTES.JET} className="nav-link" />}>
            Jet
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.VMS_TIMESHEET_UPLOAD} className="nav-link" />
            }
          >
            VMS Timesheet Report Upload
          </MenuItem>
          <MenuItem component={<Link to={ROUTES.VNDLY} className="nav-link" />}>
            VNDLY
          </MenuItem>
        </SubMenu>

        <SubMenu label="More Options" icon={<MdList size={15} />}>
          <MenuItem
            component={
              <Link to={ROUTES.WORKERBIZREFERRALS} className="nav-link" />
            }
          >
            Worker-{'>'}Biz Referrals
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.QUEST_DEFINITIONS} className="nav-link" />
            }
          >
            Quest Defintions
          </MenuItem>
          <MenuItem component={<Link to={ROUTES.TIPS} className="nav-link" />}>
            Tips
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.SPREADSHEETS} className="nav-link" />}
          >
            Spreadsheets
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.LISTINGS} className="nav-link" />}
          >
            Listings
          </MenuItem>
          <MenuItem
            component={<Link to="/upcoming-listings" className="nav-link" />}
          >
            Upcoming Listings
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.EVERIFY_UNPROCESS_FORMS} className="nav-link" />
            }
          >
            eVerify Unprocessed Forms
          </MenuItem>
          <MenuItem
            component={
              <Link
                to={ROUTES.EVERIFY_I9_REVERIFICATION_QUEUE}
                className="nav-link"
              />
            }
          >
            I-9 Reverification Queue
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.WORKER_SUPPLY} className="nav-link" />}
          >
            Worker Supply
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.USER_PERMISSIONS} className="nav-link" />
            }
          >
            User Permissions
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.INVOICE_SYNC} className="nav-link" />}
          >
            Invoice Sync
          </MenuItem>
          <MenuItem
            component={<Link to={ROUTES.TIME_CLOCKS} className="nav-link" />}
          >
            Time Clocks
          </MenuItem>
          <MenuItem
            component={
              <Link to={ROUTES.COMPUMATIC_CLOCK_PUNCHES} className="nav-link" />
            }
          >
            Compumatic Clock Punches
          </MenuItem>
        </SubMenu>
        <SignOutButton />
      </Menu>
    </div>
  )
}

const NavigationNonAuth = () => <div />

export default SidebarNav

const StyledSidebarHeader = styled.div`
  height: 64px;
  min-height: 64px;
  display: flex;
  align-items: center;
  padding: 0 20px;

  > div {
    width: 100%;
    overflow: hidden;
  }
`

const HamburgerContainer = styled.div`
  padding: 0 16px;
  width: 100%;
  display: flex;
  align-items: center;
  height: 50px;
`

const HamburgerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: ${theme.menu.icon};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`

const CommandPaletteInputContainer = styled.div<{ collapsed?: boolean }>`
  position: relative;
  margin: ${({ collapsed }) => (collapsed ? '12px' : '8px 16px')};
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};
`

const CollapsedSearchButton = styled.button`
  width: 40px;
  height: 36px;
  background-color: #f5f5f5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;

  &:focus {
    background-color: #ffffff;
    box-shadow: 0 0 0 2px rgba(39, 200, 182, 0.2);
    outline: none;
  }
`

const CommandPaletteInput = styled.input`
  width: 100%;
  padding: 10px 80px 10px 46px;
  background-color: #f5f5f5;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  outline: none;
  transition: all 0.2s;

  &::placeholder {
    color: #999;
  }

  &:hover {
    background-color: #eeeeee;
  }

  &:focus {
    background-color: #ffffff;
    box-shadow: 0 0 0 2px rgba(39, 200, 182, 0.2);
  }
`

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  color: #999;
  display: flex;
  align-items: center;
  pointer-events: none;
`

const ShortcutHint = styled.span`
  position: absolute;
  right: 12px;
  color: #999;
  font-size: 12px;
  pointer-events: none;
`

SidebarNavAuth.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  setCollapsed: PropTypes.func.isRequired,
}
